import React from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen } from '@fortawesome/pro-light-svg-icons/faBookOpen';
import { faRobot } from '@fortawesome/pro-light-svg-icons/faRobot';
import { faGaugeHigh } from '@fortawesome/pro-light-svg-icons/faGaugeHigh';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { TypedMemo } from '../../../common/types';
import { API_DOCS_URL } from './apiDocsUrl';
import { API_BASE_URL } from './apiBaseUrl';
import { ApiCopyButton } from './ApiCopyButton';
import { ApiDocsButton } from './ApiDocsButton';
import { Panel, PanelBody, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

const SAMPLE_INPUT = encodeURIComponent(JSON.stringify({ json: { limit: 5 } }));
const SAMPLE_URL = `${API_BASE_URL}/artists.topGaining?input=${SAMPLE_INPUT}`;
const SAMPLE_AUTH = `'Authorization: Token m24_your_token_here'`;
const SAMPLE_SNIPPET = `curl '${SAMPLE_URL}' \\\n  -H ${SAMPLE_AUTH}`;

const linkMessages = defineMessages({
  endpoints: { id: 'app.sml.settings.apiAccess.quick.endpoints', defaultMessage: 'Endpoint reference' },
  mcp: { id: 'app.sml.settings.apiAccess.quick.mcp', defaultMessage: 'MCP for AI clients' },
  limits: { id: 'app.sml.settings.apiAccess.quick.limits', defaultMessage: 'Limits and errors' },
});

interface QuickLink {
  key: string;
  label: MessageDescriptor;
  href: string;
  icon: IconDefinition;
}

const links: QuickLink[] = [
  { key: 'endpoints', label: linkMessages.endpoints, href: `${API_DOCS_URL}#endpoints`, icon: faBookOpen },
  { key: 'mcp', label: linkMessages.mcp, href: `${API_DOCS_URL}#mcp`, icon: faRobot },
  { key: 'limits', label: linkMessages.limits, href: `${API_DOCS_URL}#limits`, icon: faGaugeHigh },
];

/** The shortest path from "I have a token" to "I got JSON back". */
const ApiQuickstartComponent: React.FC = () => {
  const intl = useIntl();

  return (
    <Panel as="section" className={styles.section}>
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.settings.apiAccess.quick.title', defaultMessage: 'Quickstart' })}
        right={
          <>
            <span className={styles.hint}>
              {intl.formatMessage(
                { id: 'app.sml.settings.apiAccess.quick.baseUrl', defaultMessage: 'Base URL · {url}' },
                { url: API_BASE_URL }
              )}
            </span>
            <ApiDocsButton size="sm" />
          </>
        }
      />
      <PanelBody className={styles.quick}>
        <div className={styles.code}>
          <div className={styles.codeHead}>
            <span className={styles.codeLang}>cURL</span>
            <ApiCopyButton
              text={SAMPLE_SNIPPET}
              label={intl.formatMessage({
                id: 'app.sml.settings.apiAccess.quick.copySnippet',
                defaultMessage: 'Copy the example request',
              })}
            />
          </div>
          <pre>
            curl <span className={styles.codeUrl}>&apos;{SAMPLE_URL}&apos;</span> \{'\n  '}
            <span className={styles.codeFlag}>-H</span> <span className={styles.codeString}>{SAMPLE_AUTH}</span>
          </pre>
        </div>
        <div className={styles.links}>
          {links.map((link) => (
            <a key={link.key} className={styles.link} href={link.href} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={link.icon} className={styles.linkIcon} />
              <b>{intl.formatMessage(link.label)}</b>
              <FontAwesomeIcon icon={faChevronRight} className={styles.linkArrow} />
            </a>
          ))}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const ApiQuickstart = TypedMemo(ApiQuickstartComponent);
